import React, { useState, useEffect } from 'react';
import { 
  LayoutDashboard, Users, DollarSign, TrendingUp, Calendar, 
  Music, Settings as SettingsIcon, LogOut, Menu, X, FileText, Moon, Sun, Cake, BookOpen, Megaphone
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { useTheme } from '../../contexts/ThemeContext';
import { ROLES, CHURCH_INFO } from '../../utils/constants';
import { hasPermission, getInitials, hasModuleAccess } from '../../utils/helpers';

// Import NotificationBell
import NotificationBell from '../common/NotificationBell';

// Import des vues
import DashboardView from './DashboardView';
import TransactionsView from '../finances/TransactionsView';
import MembersView from '../members/MembersView';
import ProgrammesCalendarView from '../programmes/ProgrammesCalendarView';
import FihiranaList from '../fihirana/FihiranaList';
import FinancialReports from '../reports/FinancialReports';
import Settings from '../settings/Settings';
import BirthdaysView from '../birthdays/BirthdaysView';
import ChurchJournal from '../journal/ChurchJournal';
import AnnouncementsView from '../announcements/AnnouncementsView'; // 🆕 NOUVEAU
import { logger, performance, logFirebaseError } from '../../utils/logger';


const Dashboard = () => {
  const { currentUser, logout } = useAuth();
  const { data } = useData();
  const { isDarkMode, toggleTheme } = useTheme();
  const [currentView, setCurrentView] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const role = ROLES[currentUser?.role] || ROLES.membre;

  // Navigation items avec icônes exactes
  const navItems = [
    { id: 'dashboard', label: 'Tableau de bord', icon: LayoutDashboard, permission: null },
    { id: 'announcements', label: 'Annonces', icon: Megaphone, permission: null }, // 🆕 NOUVEAU
    { id: 'programmes', label: 'Programmes', icon: Calendar, permission: 'view_programmes' },
    { id: 'fihirana', label: 'Fihirana', icon: Music, permission: 'view_fihirana' },
    { id: 'birthdays', label: 'Anniversaires', icon: Cake, permission: null },
    { id: 'journal', label: 'Journal', icon: BookOpen, permission: 'view_programmes' },
    { id: 'transactions', label: 'Transactions', icon: DollarSign, permission: 'view_finances' },
    { id: 'members', label: 'Membres', icon: Users, permission: 'view_members' },
    { id: 'reports', label: 'Rapports', icon: FileText, permission: 'export_all' },
    { id: 'settings', label: 'Paramètres', icon: SettingsIcon, permission: null }
  ];

  const checkPermission = (permission, moduleId) => {
    if (moduleId && !hasModuleAccess(currentUser, moduleId)) {
      return false;
    }
    
    if (!permission) return true;
    
    if (currentUser?.role === 'admin') return true;
    
    if (currentUser?.permissions?.includes('all')) return true;
    
    return hasPermission(currentUser, permission);
  };

  const visibleNavItems = navItems.filter(item => checkPermission(item.permission, item.id));

  const handleLogout = () => {
    if (window.confirm('Êtes-vous sûr de vouloir vous déconnecter ?')) {
      logout();
    }
  };

  const handleNavigate = (path) => {
    logger.log('📍 Navigation vers:', path);
    const view = path.split('?')[0].replace('/', '') || 'dashboard';
    setCurrentView(view);
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? 'dark' : ''}`}>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        {/* Sidebar */}
        <aside className={`
          fixed top-0 left-0 h-full bg-gradient-to-b from-[#2D3B87] to-[#1e2563] shadow-lg z-40 w-64 
          transform transition-transform duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0
        `}>
          {/* Logo Section */}
          <div className="p-6 border-b border-white/10 flex items-center justify-center">
            <img 
              src="/logo-vomm-real.jpg" 
              alt="VOMM FJKM Hannover" 
              className="h-20 w-auto object-contain"
            />
          </div>

          {/* Navigation */}
          <nav className="p-4 space-y-1 overflow-y-auto h-[calc(100vh-180px)]">
            {visibleNavItems.map(item => {
              const Icon = item.icon;
              const isActive = currentView === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    setCurrentView(item.id);
                    setSidebarOpen(false);
                  }}
                  className={`
                    w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200
                    ${isActive 
                      ? 'bg-white text-[#2D3B87] font-semibold shadow-lg' 
                      : 'text-white/90 hover:bg-white/10'
                    }
                  `}
                >
                  <Icon size={20} />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>

          {/* Déconnexion */}
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
            <button
              onClick={handleLogout}
              className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-white/90 hover:bg-white/10 transition-colors"
            >
              <LogOut size={20} />
              <span>Déconnexion</span>
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <div className="md:ml-64 min-h-screen">
          {/* Header */}
          <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 sticky top-0 z-30">
            <div className="flex items-center justify-between p-4">
              {/* Menu mobile */}
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="md:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
              </button>

              {/* Info utilisateur et notifications */}
              <div className="flex items-center gap-4 ml-auto">
                {/* Theme Toggle */}
                <button
                  onClick={toggleTheme}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  title={isDarkMode ? 'Mode clair' : 'Mode sombre'}
                >
                  {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
                </button>

                {/* Notification Bell */}
                <NotificationBell onNavigate={handleNavigate} />

                {/* User info */}
                <div className="flex items-center gap-3">
                  <div className="text-right hidden sm:block">
                    <p className="text-sm font-semibold text-gray-800 dark:text-white">
                      {currentUser?.name}
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      {role.label}
                    </p>
                  </div>
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold">
                    {getInitials(currentUser?.name)}
                  </div>
                </div>
              </div>
            </div>
          </header>

          {/* Content Area */}
          <main className="p-6">
            {currentView === 'dashboard' && <DashboardView onNavigate={handleNavigate} />}
            {currentView === 'announcements' && <AnnouncementsView />}
            {currentView === 'programmes' && hasModuleAccess(currentUser, 'programmes') && <ProgrammesCalendarView />}
            {currentView === 'fihirana' && hasModuleAccess(currentUser, 'fihirana') && <FihiranaList />}
            {currentView === 'birthdays' && <BirthdaysView />}
            {currentView === 'journal' && hasModuleAccess(currentUser, 'journal') && <ChurchJournal />}
            {currentView === 'transactions' && hasModuleAccess(currentUser, 'transactions') && <TransactionsView />}
            {currentView === 'members' && hasModuleAccess(currentUser, 'members') && <MembersView />}
            {currentView === 'reports' && hasModuleAccess(currentUser, 'reports') && <FinancialReports />}
            {currentView === 'settings' && <Settings />}

            {/* Messages d'accès refusé */}
            {currentView === 'programmes' && !hasModuleAccess(currentUser, 'programmes') && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 text-center">
                <div className="text-red-500 mb-4">
                  <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-2">
                  Accès Refusé
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Vous n'avez pas les permissions nécessaires pour accéder à ce module.
                </p>
              </div>
            )}
            {currentView === 'fihirana' && !hasModuleAccess(currentUser, 'fihirana') && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 text-center">
                <div className="text-red-500 mb-4">
                  <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-2">
                  Accès Refusé
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Vous n'avez pas les permissions nécessaires pour accéder à ce module.
                </p>
              </div>
            )}
            {currentView === 'journal' && !hasModuleAccess(currentUser, 'journal') && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 text-center">
                <div className="text-red-500 mb-4">
                  <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-2">
                  Accès Refusé
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Vous n'avez pas les permissions nécessaires pour accéder à ce module.
                </p>
              </div>
            )}
            {currentView === 'transactions' && !hasModuleAccess(currentUser, 'transactions') && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 text-center">
                <div className="text-red-500 mb-4">
                  <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-2">
                  Accès Refusé
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Vous n'avez pas les permissions nécessaires pour accéder à ce module.
                </p>
              </div>
            )}
            {currentView === 'members' && !hasModuleAccess(currentUser, 'members') && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 text-center">
                <div className="text-red-500 mb-4">
                  <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-2">
                  Accès Refusé
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Vous n'avez pas les permissions nécessaires pour accéder à ce module.
                </p>
              </div>
            )}
            {currentView === 'reports' && !hasModuleAccess(currentUser, 'reports') && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 text-center">
                <div className="text-red-500 mb-4">
                  <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-2">
                  Accès Refusé
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Vous n'avez pas les permissions nécessaires pour accéder à ce module.
                </p>
              </div>
            )}
          </main>
        </div>

        {/* Overlay mobile */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-30 md:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </div>
    </div>
  );
};

export default Dashboard;